import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Search, Plus, Edit, Trash2, UserPlus, Shield, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { useAppData } from '../hooks/useAppData';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { v4 as uuidv4 } from 'uuid';

export default function UsuariosTable() {
  const { usuarios, addUsuario, updateUsuario, deleteUsuario } = useAppData();
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<any>(null);
  const [form, setForm] = useState({
    nombre: '',
    tipo: 'vendedor', // vendedor, comprador, admin, contabilidad, socio
    pin: '', // Para vendedor, comprador, admin
    password: '', // Para contabilidad, socio
    activo: true,
    bloqueado: false
  });
  const [error, setError] = useState<string | null>(null);

  // Resetear formulario al abrir/cerrar modal
  useEffect(() => {
    if (!modalOpen) {
      setForm({ nombre: '', tipo: 'vendedor', pin: '', password: '', activo: true, bloqueado: false });
      setEditingUser(null);
      setError(null);
    }
  }, [modalOpen]);

  // Cargar datos al editar
  useEffect(() => {
    if (editingUser) {
      setForm({
        nombre: editingUser.nombre,
        tipo: editingUser.tipo,
        pin: editingUser.pin || '',
        password: editingUser.password || '',
        activo: editingUser.activo !== undefined ? editingUser.activo : true,
        bloqueado: editingUser.bloqueado !== undefined ? editingUser.bloqueado : false
      });
    }
  }, [editingUser]);

  const filteredUsers = usuarios.filter((u: any) =>
    u.nombre.toLowerCase().includes(search.toLowerCase()) ||
    u.tipo.toLowerCase().includes(search.toLowerCase())
  );

  const handleSave = async () => {
    if (!form.nombre.trim()) {
      setError('El nombre es obligatorio.');
      return;
    }
    if ((form.tipo === 'vendedor' || form.tipo === 'comprador' || form.tipo === 'admin') && !form.pin.trim()) {
      setError('El PIN es obligatorio para este tipo de usuario.');
      return;
    }
    if ((form.tipo === 'contabilidad' || form.tipo === 'socio') && !form.password.trim()) {
      setError('La contraseña es obligatoria para este tipo de usuario.');
      return;
    }

    try {
      const userData = {
        nombre: form.nombre.trim(),
        tipo: form.tipo,
        pin: (form.tipo === 'vendedor' || form.tipo === 'comprador' || form.tipo === 'admin') ? form.pin.trim() : undefined,
        password: (form.tipo === 'contabilidad' || form.tipo === 'socio') ? form.password.trim() : undefined,
        activo: form.activo,
        bloqueado: form.bloqueado
      };

      if (editingUser) {
        await updateUsuario(editingUser.id, userData);
      } else {
        await addUsuario({ ...userData, id: uuidv4(), createdAt: new Date().toISOString() });
      }
      setModalOpen(false);
    } catch (e) {
      console.error('Error al guardar usuario:', e);
      setError('Error al guardar el usuario.');
    }
  };

  const handleDelete = async () => {
    if (userToDelete) {
      try {
        await deleteUsuario(userToDelete.id);
        setDeleteConfirmOpen(false);
        setUserToDelete(null);
      } catch (e) {
        console.error('Error al eliminar usuario:', e);
        alert('Error al eliminar el usuario.');
      }
    }
  };

  const getTipoColor = (tipo: string) => {
    switch (tipo) {
      case 'admin': return 'bg-purple-500/10 text-purple-500 border-purple-500/20';
      case 'contabilidad': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'socio': return 'bg-indigo-500/10 text-indigo-500 border-indigo-500/20';
      case 'vendedor': return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
      case 'comprador': return 'bg-orange-500/10 text-orange-500 border-orange-500/20';
      default: return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  if (!usuarios) return (
    <Card className="bg-card border-border shadow-none rounded-lg">
      <CardContent className="p-6 text-center text-muted-foreground">Cargando usuarios...</CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-foreground">Gestión de Usuarios</h2>
          <p className="text-muted-foreground mt-1 text-[13px]">Administra los accesos y roles del sistema</p>
        </div>
        <Button onClick={() => { setEditingUser(null); setModalOpen(true); }} className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-none border-0">
          <UserPlus className="h-4 w-4 mr-2" />
          Nuevo Usuario
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/70" />
          <Input
            placeholder="Buscar usuarios..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-8 bg-transparent border border-input hover:border-ring focus:border-ring rounded-[6px] h-8 text-[13px] text-foreground placeholder:text-muted-foreground/40 shadow-none transition-colors"
          />
        </div>
      </div>

      <Card className="bg-card border-border shadow-none rounded-lg overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-b border-border/50">
                  <TableHead className="px-4 py-3 text-[11px] font-medium text-muted-foreground h-10">Usuario</TableHead>
                  <TableHead className="px-4 py-3 text-[11px] font-medium text-muted-foreground h-10">Rol</TableHead>
                  <TableHead className="px-4 py-3 text-[11px] font-medium text-muted-foreground h-10">Credenciales</TableHead>
                  <TableHead className="px-4 py-3 text-[11px] font-medium text-muted-foreground h-10">Estado</TableHead>
                  <TableHead className="px-4 py-3 text-[11px] font-medium text-muted-foreground h-10 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      No se encontraron usuarios
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user: any) => (
                    <TableRow key={user.id} className="border-b border-border/50 hover:bg-muted/50 transition-colors">
                      <TableCell className="px-4 py-3 font-medium text-foreground">{user.nombre}</TableCell>
                      <TableCell className="px-4 py-3">
                        <Badge variant="outline" className={`capitalize font-medium border ${getTipoColor(user.tipo)}`}>
                          {user.tipo}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-4 py-3 text-sm text-muted-foreground">
                        {user.pin ? (
                          <span className="flex items-center gap-1"><Shield className="h-3 w-3" /> PIN: ••••</span>
                        ) : (
                          <span className="flex items-center gap-1"><Shield className="h-3 w-3" /> Pass: ••••••••</span>
                        )}
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        {user.activo ? (
                          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20 flex w-fit items-center gap-1">
                            <CheckCircle className="h-3 w-3" /> Activo
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/20 flex w-fit items-center gap-1">
                            <XCircle className="h-3 w-3" /> Inactivo
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setEditingUser(user); setModalOpen(true); }}
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground hover:bg-accent"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setUserToDelete(user); setDeleteConfirmOpen(true); }}
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Modal Crear/Editar Usuario */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-popover border-border sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-foreground">{editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {editingUser ? 'Modifica los datos del usuario seleccionado.' : 'Ingresa los datos para crear un nuevo usuario.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="nombre" className="text-foreground">Nombre</Label>
              <Input
                id="nombre"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                placeholder="Nombre completo"
                className="bg-secondary border-input focus:border-ring text-foreground placeholder:text-muted-foreground"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="tipo" className="text-foreground">Rol</Label>
              <Select
                value={form.tipo}
                onValueChange={(value) => setForm({ ...form, tipo: value })}
              >
                <SelectTrigger className="bg-secondary border-input focus:border-ring text-foreground">
                  <SelectValue placeholder="Selecciona un rol" />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="vendedor">Vendedor</SelectItem>
                  <SelectItem value="comprador">Comprador</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="contabilidad">Contabilidad</SelectItem>
                  <SelectItem value="socio">Socio</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(form.tipo === 'vendedor' || form.tipo === 'comprador' || form.tipo === 'admin') && (
              <div className="grid gap-2">
                <Label htmlFor="pin" className="text-foreground">PIN de Acceso</Label>
                <Input
                  id="pin"
                  value={form.pin}
                  onChange={(e) => setForm({ ...form, pin: e.target.value })}
                  placeholder="Ej. 1234"
                  type="password"
                  className="bg-secondary border-input focus:border-ring text-foreground placeholder:text-muted-foreground"
                />
              </div>
            )}

            {(form.tipo === 'contabilidad' || form.tipo === 'socio') && (
              <div className="grid gap-2">
                <Label htmlFor="password" className="text-foreground">Contraseña</Label>
                <Input
                  id="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Contraseña segura"
                  type="password"
                  className="bg-secondary border-input focus:border-ring text-foreground placeholder:text-muted-foreground"
                />
              </div>
            )}

            <div className="flex items-center gap-4 mt-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="activo"
                  checked={form.activo}
                  onChange={(e) => setForm({ ...form, activo: e.target.checked })}
                  className="rounded border-input bg-secondary text-primary focus:ring-ring"
                />
                <Label htmlFor="activo" className="text-foreground cursor-pointer">Usuario Activo</Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="bloqueado"
                  checked={form.bloqueado}
                  onChange={(e) => setForm({ ...form, bloqueado: e.target.checked })}
                  className="rounded border-input bg-secondary text-primary focus:ring-ring"
                />
                <Label htmlFor="bloqueado" className="text-foreground cursor-pointer">Bloqueado</Label>
              </div>
            </div>

            {error && <p className="text-sm text-destructive font-medium">{error}</p>}
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setModalOpen(false)} className="border-input hover:bg-accent text-foreground">
              Cancelar
            </Button>
            <Button onClick={handleSave} className="bg-primary text-primary-foreground hover:bg-primary/90">
              Guardar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Confirmar Eliminación */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="bg-popover border-border sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Confirmar Eliminación
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              ¿Estás seguro de que deseas eliminar al usuario <b>{userToDelete?.nombre}</b>? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)} className="border-input hover:bg-accent text-foreground">
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Eliminar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}